import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

interface Topic { id: number; topicName: string; explanation: string; realWorldExample: string; }
interface Question {
  questionNumber: number;
  questionId: number | null;
  source: string;
  questionText: string;
  optionA: string; optionB: string; optionC: string; optionD: string;
  correctOption: string; explanation: string;
}

type Screen = 'topics' | 'difficulty' | 'loading' | 'quiz' | 'result';
type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

const MathQuiz: React.FC<{ kidId?: number | string; kidGrade?: number }> = ({ kidId, kidGrade: propGrade }) => {
  const { userId, grade: myGrade } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const kidGrade = propGrade ?? myGrade;
  const [screen, setScreen] = useState<Screen>('topics');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [current, setCurrent] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [grade, setGrade] = useState(kidGrade || 1);
  const [difficulty, setDifficulty] = useState<Difficulty>('MEDIUM');

  useEffect(() => { fetchTopics(grade); }, [grade]);

  const fetchTopics = async (g: number) => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/math/topics?grade=${g}`);
      setTopics(res.data);
    } catch {}
  };

  const startQuiz = (topic: Topic) => {
    setSelectedTopic(topic);
    setDifficulty('MEDIUM');
    setScreen('difficulty');
  };

  const beginQuiz = async (selectedDifficulty: Difficulty) => {
    setDifficulty(selectedDifficulty);
    setScreen('loading');
    setAnswers({});
    setCurrent(0);
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/math/quiz/${selectedTopic!.id}?difficulty=${selectedDifficulty}`);
      setQuestions(res.data);
      setStartTime(Date.now());
      setScreen('quiz');
    } catch {
      alert('Failed to load quiz. Please try again.');
      setScreen('topics');
    }
  };

  const selectAnswer = (option: string) => {
    setAnswers(prev => ({ ...prev, [current]: option }));
  };

  const nextQuestion = () => {
    if (current < questions.length - 1) setCurrent(c => c + 1);
  };

  const submitQuiz = async () => {
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    let score = 0;

    const answerDetails = questions.map((q, i) => {
      const selected = answers[i] || '';
      const correct = selected === q.correctOption;
      if (correct) score++;
      return {
        questionNumber: i + 1,
        questionId: q.questionId || null,
        source: q.source || 'CLAUDE',
        questionText: q.questionText,
        optionA: q.optionA, optionB: q.optionB,
        optionC: q.optionC, optionD: q.optionD,
        correctOption: q.correctOption,
        selectedOption: selected,
        correct,
        explanation: q.explanation || '',
      };
    });

    try {
      const res = await api.post(`/api/kids/${effectiveKidId}/math/quiz/${selectedTopic!.id}/submit`, {
        score, totalQuestions: questions.length,
        timeTaken, answers: answerDetails
      });
      setResult({ ...res.data, questions, answers });
      setScreen('result');
    } catch {
      alert('Failed to submit. Please try again.');
    }
  };

  const percentage = result ? Math.round(result.percentage) : 0;
  const emoji = percentage >= 80 ? '🌟' : percentage >= 60 ? '😊' : '💪';

  if (screen === 'difficulty') return (
    <div>
      <header className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
        <button onClick={() => setScreen('topics')} className="text-orange-500 font-medium">← Back</button>
        <h1 className="text-lg font-bold text-orange-500">{selectedTopic?.topicName}</h1>
        <span />
      </header>
      <main className="max-w-md mx-auto p-6">
        <h2 className="text-center text-xl font-bold text-gray-700 mb-2">Choose Difficulty</h2>
        <p className="text-center text-sm text-gray-400 mb-8">How challenging do you want the quiz to be?</p>
        <div className="space-y-4">
          {[
            { level: 'EASY' as Difficulty, emoji: '🟢', label: 'Easy', desc: 'Simple questions to build confidence', color: 'border-green-400 hover:bg-green-50', badge: 'bg-green-100 text-green-700' },
            { level: 'MEDIUM' as Difficulty, emoji: '🟡', label: 'Medium', desc: 'Balanced mix of questions', color: 'border-yellow-400 hover:bg-yellow-50', badge: 'bg-yellow-100 text-yellow-700' },
            { level: 'HARD' as Difficulty, emoji: '🔴', label: 'Hard', desc: 'Challenging questions to push your skills', color: 'border-red-400 hover:bg-red-50', badge: 'bg-red-100 text-red-700' },
          ].map(d => (
            <button
              key={d.level}
              onClick={() => beginQuiz(d.level)}
              className={`w-full bg-white border-2 ${d.color} rounded-2xl p-5 flex items-center gap-4 transition text-left`}
            >
              <span className="text-4xl">{d.emoji}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-gray-800 text-lg">{d.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${d.badge}`}>{d.level}</span>
                </div>
                <p className="text-sm text-gray-500">{d.desc}</p>
              </div>
              <span className="text-gray-300 text-2xl">›</span>
            </button>
          ))}
        </div>
      </main>
    </div>
  );

  if (screen === 'loading') return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="text-center">
        <div className="text-5xl mb-4 animate-bounce">🧮</div>
        <p className="text-gray-600 font-medium">Claude is generating your quiz...</p>
        <p className="text-sm text-gray-400 mt-1">This may take a few seconds</p>
      </div>
    </div>
  );

  if (screen === 'result') return (
    <div>
      <header className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
        <button onClick={() => setScreen('topics')} className="text-orange-500 font-medium">← Topics</button>
        <h1 className="text-lg font-bold text-orange-500">Quiz Result</h1>
        <span />
      </header>
      <main className="max-w-xl mx-auto p-6 space-y-4">
        <div className="bg-white rounded-2xl shadow p-6 text-center">
          <div className="text-6xl mb-2">{emoji}</div>
          <h2 className="text-2xl font-bold text-gray-800">{percentage}%</h2>
          <p className="text-gray-500">{result.score} / {result.totalQuestions} correct</p>
          <p className="text-sm text-gray-400 mt-1">{selectedTopic?.topicName}</p>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
            <div className="bg-orange-500 h-3 rounded-full transition-all"
              style={{ width: `${percentage}%` }} />
          </div>
        </div>

        {/* Answer review */}
        <h3 className="font-semibold text-gray-700">Review Answers</h3>
        {result.questions.map((q: Question, i: number) => {
          const selected = result.answers[i];
          const correct = q.correctOption;
          const isRight = selected === correct;
          return (
            <div key={i} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${isRight ? 'border-green-400' : 'border-red-400'}`}>
              <p className="font-medium text-gray-800 mb-2">{i + 1}. {q.questionText}</p>
              {['A', 'B', 'C', 'D'].map(opt => (
                <div key={opt} className={`text-sm px-3 py-1 rounded-lg mb-1
                  ${opt === correct ? 'bg-green-100 text-green-700 font-semibold' :
                    opt === selected && !isRight ? 'bg-red-100 text-red-600' : 'text-gray-600'}`}>
                  {opt}. {(q as any)[`option${opt}`]}
                </div>
              ))}
              {q.explanation && <p className="text-xs text-gray-400 mt-2 italic">{q.explanation}</p>}
            </div>
          );
        })}

        <button onClick={() => setScreen('topics')}
          className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold">
          Try Another Topic
        </button>
      </main>
    </div>
  );

  if (screen === 'quiz' && questions.length > 0) {
    const q = questions[current];
    const allAnswered = Object.keys(answers).length === questions.length;
    return (
      <div>
        <header className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
          <button onClick={() => setScreen('topics')} className="text-orange-500 font-medium">✕ Exit</button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-orange-500">{selectedTopic?.topicName}</h1>
            <span className="text-xs text-gray-400">{difficulty}</span>
          </div>
          <span className="text-sm text-gray-500">{current + 1}/{questions.length}</span>
        </header>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 h-1.5">
          <div className="bg-orange-500 h-1.5 transition-all"
            style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
        </div>

        <main className="max-w-xl mx-auto p-6 space-y-4">
          <div className="bg-white rounded-2xl shadow p-5">
            <p className="font-semibold text-gray-800 text-lg mb-4">{q.questionText}</p>
            {['A', 'B', 'C', 'D'].map(opt => (
              <button key={opt} onClick={() => selectAnswer(opt)}
                className={`w-full text-left px-4 py-3 rounded-xl mb-2 border-2 transition font-medium
                  ${answers[current] === opt
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-orange-300 text-gray-700'}`}>
                <span className="font-bold mr-2">{opt}.</span>
                {(q as any)[`option${opt}`]}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            {current > 0 && (
              <button onClick={() => setCurrent(c => c - 1)}
                className="flex-1 border-2 border-orange-300 text-orange-500 py-3 rounded-xl font-semibold">
                ← Back
              </button>
            )}
            {current < questions.length - 1 ? (
              <button onClick={nextQuestion} disabled={!answers[current]}
                className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold disabled:opacity-40">
                Next →
              </button>
            ) : (
              <button onClick={submitQuiz} disabled={!allAnswered}
                className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold disabled:opacity-40">
                Submit Quiz ✓
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <main className="max-w-xl mx-auto p-6 space-y-4">
        <h1 className="text-lg font-bold text-orange-500 mb-2">Math Quiz</h1>
        <div>
          <label className="text-sm font-medium text-gray-600">Select Grade</label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {[1,2,3,4,5,6,7,8,9,10].map(g => (
              <button key={g} onClick={() => setGrade(g)}
                className={`w-10 h-10 rounded-full text-sm font-semibold transition
                  ${grade === g ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-400'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {topics.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <div className="text-4xl mb-2">🧮</div>
            <p>No topics available for Grade {grade} yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="font-semibold text-gray-700">Grade {grade} Topics</h2>
            {topics.map(topic => (
              <div key={topic.id} className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{topic.topicName}</p>
                    {topic.explanation && <p className="text-sm text-gray-500 mt-1">{topic.explanation}</p>}
                    {topic.realWorldExample && (
                      <p className="text-xs text-orange-500 mt-1">💡 {topic.realWorldExample}</p>
                    )}
                  </div>
                  <button onClick={() => startQuiz(topic)}
                    className="ml-3 bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded-lg font-medium transition">
                    Start Quiz
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MathQuiz;
